import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/api";
import { rupee, effectivePrice } from "@/lib/format";
import { usePageMeta } from "@/hooks/usePageMeta";
import { FrequentlyBoughtTogether } from "@/components/FrequentlyBoughtTogether";
import { Button } from "@/components/ui/button";
import { ShoppingBasket, Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";

export default function Cart() {
  usePageMeta("Your Cart", "Review your order before checkout.");
  const { items, updateQty, removeItem, subtotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const checkout = () => {
    if (!user) {
      navigate("/login", { state: { from: "/checkout" } });
      return;
    }
    navigate("/checkout");
  };

  if (items.length === 0)
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="cart-empty">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-cream-alt text-ink-muted">
          <ShoppingBag className="h-8 w-8" />
        </span>
        <h1 className="mt-6 font-heading text-2xl text-ink">Your cart is empty</h1>
        <p className="mt-2 text-ink-soft">Add some items to get started.</p>
        <Link to="/products">
          <Button className="mt-6 rounded-full bg-brand px-8 text-ink hover:bg-brand-hover">Browse Products</Button>
        </Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="font-heading text-3xl font-bold text-ink">Your Cart</h1>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {items.map((item) => {
            const img = fileUrl(item.image_id);
            return (
              <motion.div
                key={item.id}
                layout
                className="flex items-center gap-4 rounded-2xl border border-border/60 bg-white p-4 shadow-ambient"
                data-testid={`cart-item-${item.id}`}
              >
                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-cream-alt">
                  {img ? (
                    <img src={img} alt={item.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-ink-muted">
                      <ShoppingBasket className="h-7 w-7" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading text-lg text-ink line-clamp-1">{item.name}</h3>
                  <p className="text-sm text-ink-muted">{rupee(effectivePrice(item))} / {item.unit}</p>
                  <div className="mt-2 flex items-center gap-3">
                    <div className="flex items-center rounded-full border border-border">
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => updateQty(item.id, item.quantity - 1)} data-testid={`cart-decrease-${item.id}`}>
                        <Minus className="h-3.5 w-3.5" />
                      </Button>
                      <span className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => updateQty(item.id, item.quantity + 1)} data-testid={`cart-increase-${item.id}`}>
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <button onClick={() => removeItem(item.id)} className="text-ink-muted transition-colors hover:text-destructive" data-testid={`cart-remove-${item.id}`} aria-label="Remove">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="text-right font-semibold text-ink">{rupee(effectivePrice(item) * item.quantity)}</div>
              </motion.div>
            );
          })}
        </div>

        <aside className="h-fit rounded-2xl border border-border/60 bg-white p-6 shadow-ambient">
          <h2 className="font-heading text-xl text-ink">Order Summary</h2>
          <div className="mt-4 flex justify-between text-ink-soft">
            <span>Subtotal</span>
            <span className="font-semibold text-ink" data-testid="cart-subtotal">{rupee(subtotal)}</span>
          </div>
          <p className="mt-1 text-xs text-ink-muted">Delivery & discounts applied at checkout.</p>
          <Button onClick={checkout} data-testid="proceed-checkout" className="mt-6 w-full rounded-full bg-brand text-ink hover:bg-brand-hover">
            Proceed to Checkout <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
          <Link to="/products" className="mt-3 block text-center text-sm text-ink-muted hover:text-brand">
            Continue shopping
          </Link>
        </aside>
      </div>

      <div className="mt-10 space-y-8">
        <FrequentlyBoughtTogether cartProductIds={items.map((i) => i.id)} />
        <FrequentlyBoughtTogether
          cartProductIds={items.map((i) => i.id)}
          apiPath="/products/you-may-like"
          title="You might also like"
          subtitle="Popular picks similar to your cart"
        />
      </div>
    </div>
  );
}
